import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2, X, Save, GripVertical, Megaphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface LeadKaynak {
    id: number;
    ad: string;
    sira: number;
    aktifMi: boolean;
    eklenmeTarihi: string;
}

const emptyForm = { ad: '', aktifMi: true };

const PazarlamaAraclari: React.FC = () => {
    const { t } = useTranslation();
    const [kaynaklar, setKaynaklar] = useState<LeadKaynak[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selected, setSelected] = useState<LeadKaynak | null>(null);
    const [form, setForm] = useState(emptyForm);
    const [dragId, setDragId] = useState<number | null>(null);
    const [dragOverId, setDragOverId] = useState<number | null>(null);

    const fetchKaynaklar = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/LeadKaynaklar');
            setKaynaklar(res.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchKaynaklar(); }, []);

    const openAdd = () => {
        setSelected(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (k: LeadKaynak) => {
        setSelected(k);
        setForm({ ad: k.ad, aktifMi: k.aktifMi });
        setShowModal(true);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.ad.trim()) { toast.warn('Kaynak adı zorunludur'); return; }
        setSaving(true);
        try {
            if (selected) {
                await axiosInstance.put(`/api/LeadKaynaklar/${selected.id}`, {
                    id: selected.id,
                    ad: form.ad,
                    aktifMi: form.aktifMi,
                    sira: selected.sira,
                    eklenmeTarihi: selected.eklenmeTarihi,
                });
                toast.success(t('msg.islemBasarili'));
            } else {
                await axiosInstance.post('/api/LeadKaynaklar', {
                    ad: form.ad,
                    aktifMi: form.aktifMi,
                });
                toast.success(t('msg.islemBasarili'));
            }
            setShowModal(false);
            fetchKaynaklar();
        } catch {
            toast.error('Kaydedilemedi');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number, ad: string) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.post(`/api/LeadKaynaklar/delete/${id}`);
            toast.success(t('msg.islemBasarili'));
            fetchKaynaklar();
        } catch {
            toast.error('Silinemedi');
        }
    };

    const handleDragStart = (id: number) => setDragId(id);
    const handleDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setDragOverId(id); };
    const handleDrop = async () => {
        if (dragId === null || dragOverId === null || dragId === dragOverId) {
            setDragId(null); setDragOverId(null); return;
        }
        const list = [...kaynaklar];
        const fromIdx = list.findIndex(k => k.id === dragId);
        const toIdx = list.findIndex(k => k.id === dragOverId);
        const [moved] = list.splice(fromIdx, 1);
        list.splice(toIdx, 0, moved);
        setKaynaklar(list);
        setDragId(null);
        setDragOverId(null);
        try {
            await axiosInstance.post('/api/LeadKaynaklar/sirala', list.map(k => k.id));
        } catch {
            toast.error(t('msg.islemHatasi'));
            fetchKaynaklar();
        }
    };

    const card: React.CSSProperties = {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'grab',
        transition: 'box-shadow 0.15s',
        marginBottom: 8,
    };

    const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 5 };
    const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#1a2340', outline: 'none', boxSizing: 'border-box' };

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Megaphone size={18} color="#f97316" />
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Pazarlama Araçları</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>Yeni potansiyel müşteri formundaki kaynak seçenekleri</div>
                    </div>
                </div>
                <button
                    onClick={openAdd}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                    <Plus size={15} /> Kaynak Ekle
                </button>
            </div>

            {/* Info */}
            <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#92400e', marginBottom: 16 }}>
                Sürükle-bırak ile sıralama değiştirebilirsiniz. Pasif kayıtlar formda görünmez.
            </div>

            {/* List */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{t('msg.yukleniyor')}</div>
            ) : kaynaklar.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Henüz kaynak eklenmemiş</div>
            ) : (
                <div>
                    {kaynaklar.map(k => (
                        <div
                            key={k.id}
                            draggable
                            onDragStart={() => handleDragStart(k.id)}
                            onDragOver={e => handleDragOver(e, k.id)}
                            onDrop={handleDrop}
                            onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                            style={{
                                ...card,
                                boxShadow: dragId === k.id ? '0 4px 16px rgba(0,0,0,0.12)' : dragOverId === k.id ? '0 0 0 2px #f97316' : '0 1px 3px rgba(0,0,0,0.04)',
                                opacity: dragId === k.id ? 0.5 : 1,
                            }}
                        >
                            <GripVertical size={16} color="#cbd5e1" style={{ flexShrink: 0 }} />

                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{k.ad}</span>
                                {!k.aktifMi && (
                                    <span style={{ fontSize: 11, background: '#f1f5f9', color: '#94a3b8', borderRadius: 4, padding: '2px 7px', fontWeight: 500 }}>{t('durum.pasif')}</span>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                    onClick={() => openEdit(k)}
                                    title={t('btn.duzenle')}
                                    style={{ width: 30, height: 30, border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <Pencil size={13} color="#64748b" />
                                </button>
                                <button
                                    onClick={() => handleDelete(k.id, k.ad)}
                                    title={t('btn.sil')}
                                    style={{ width: 30, height: 30, border: '1px solid #fecaca', borderRadius: 6, background: '#fff5f5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <Trash2 size={13} color="#ef4444" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 14, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                        {/* Modal header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{selected ? 'Kaynağı Düzenle' : 'Yeni Kaynak'}</span>
                            <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                                <X size={18} color="#94a3b8" />
                            </button>
                        </div>
                        {/* Modal body */}
                        <form onSubmit={handleSave} style={{ padding: 20 }}>
                            <div style={{ marginBottom: 16 }}>
                                <label style={lbl}>Kaynak Adı <span style={{ color: '#ef4444' }}>*</span></label>
                                <input
                                    style={inp}
                                    value={form.ad}
                                    onChange={e => setForm({ ...form, ad: e.target.value })}
                                    placeholder={t('label.ara')}
                                    autoFocus
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                                <input
                                    type="checkbox"
                                    id="aktifMi"
                                    checked={form.aktifMi}
                                    onChange={e => setForm({ ...form, aktifMi: e.target.checked })}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                                />
                                <label htmlFor="aktifMi" style={{ fontSize: 14, color: '#374151', cursor: 'pointer' }}>Aktif (formda görünsün)</label>
                            </div>
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                                >
                                    <Save size={14} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PazarlamaAraclari;
